import React, { useState, useCallback, useEffect } from 'react';
import { motion } from 'framer-motion';
import { CheckCircle, AlertCircle } from 'lucide-react';
import CppCodeEditor from './CppCodeEditor';
import { supabase } from '../supabase/client';

interface CppCodingAnswerAreaProps {
  question: {
    id: string;
    title: string;
    content: string;
    starterCode?: string;
    referenceSolution?: string;
    timeLimitMs?: number;
    memoryLimitMb?: number;
    score?: number;
  };
  paperId: string;
  isSubmitted: boolean;
  answer?: string;
  onAnswerChange: (answer: string) => void;
}

export default function CppCodingAnswerArea({
  question,
  paperId,
  isSubmitted,
  answer,
  onAnswerChange,
}: CppCodingAnswerAreaProps) {
  const [code, setCode] = useState(answer || question.starterCode || '');
  const [initialCode] = useState(question.starterCode || '');
  const [isSaving, setIsSaving] = useState(false);
  const [submissionId, setSubmissionId] = useState<string | null>(null);

  // 检查是否已提交
  useEffect(() => {
    if (answer && answer.length > 0) {
      setSubmissionId(answer);
    }
  }, [answer]);

  // 组件挂载时检查本地缓存
  useEffect(() => {
    const cachedCode = localStorage.getItem(`cpp_code_${question.id}`);
    if (cachedCode && cachedCode !== initialCode && !submissionId) {
      // 静默恢复缓存的代码，不弹窗打扰用户
      setCode(cachedCode);
    }
  }, [question.id, initialCode, submissionId]);

  // 显示 Toast 提示
  const showToast = useCallback((message: string, type: 'success' | 'error' = 'success') => {
    const toast = document.createElement('div');
    toast.className = `fixed top-20 left-1/2 -translate-x-1/2 z-50 px-4 py-2 rounded-lg shadow-lg text-sm font-medium transition-all duration-300 ${type === 'success' ? 'bg-green-500 text-white' : 'bg-red-500 text-white'}`;
    toast.textContent = message;
    document.body.appendChild(toast);
    setTimeout(() => {
      toast.style.opacity = '0';
      setTimeout(() => toast.remove(), 300);
    }, 3000);
  }, []);

  // 保存代码
  const handleSave = useCallback(async () => {
    if (!code.trim()) {
      showToast('代码不能为空', 'error');
      return;
    }

    // 防重复提交
    if (isSaving || submissionId) {
      return;
    }

    setIsSaving(true);

    try {
      // 获取当前用户
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        showToast('请先登录', 'error');
        setIsSaving(false);
        return;
      }

      // 获取学生ID（从 profiles 表）
      const { data: profileData, error: profileError } = await supabase
        .from('profiles')
        .select('id')
        .eq('id', user.id)
        .single();

      if (profileError || !profileData) {
        showToast('获取学生信息失败', 'error');
        setIsSaving(false);
        return;
      }

      const userId = profileData.id;

      // paperId 可能是 'custom'，需要处理为 null
      const validExamId = paperId && paperId !== 'custom' ? paperId : null;

      // 创建提交记录
      const { data: submissionData, error: submissionError } = await (supabase
        .from('programming_submissions' as any)
        .insert({
          user_id: userId,
          exam_id: validExamId,
          question_id: question.id,
          question_type: 'cpp_coding',
          code_content: code,
          status: 'pending',
          submitted_at: new Date().toISOString(),
        })
        .select()
        .single() as any);

      if (submissionError) {
        // 处理冲突错误
        if (submissionError.code === '409' || submissionError.message?.includes('conflict')) {
          throw new Error('您已经提交过答案，请勿重复提交');
        }
        throw new Error(`保存提交记录失败: ${submissionError.message}`);
      }

      // 更新本地状态和父组件
      setSubmissionId(String(submissionData.id));
      onAnswerChange(String(submissionData.id));

      // 清除本地缓存
      localStorage.removeItem(`cpp_code_${question.id}`);

      showToast('代码已保存', 'success');
    } catch (err: any) {
      const errorMsg = err.message || '保存失败';
      // 保存到 localStorage 作为备份
      localStorage.setItem(`cpp_code_${question.id}`, code);
      showToast(errorMsg, 'error');
    } finally {
      setIsSaving(false);
    }
  }, [code, question.id, paperId, onAnswerChange, isSaving, submissionId, showToast]);

  // 重置代码到初始模板
  const handleReset = useCallback(() => {
    if (window.confirm('重置后将恢复到题目初始模板，当前代码将丢失，是否继续？')) {
      setCode(initialCode);
    }
  }, [initialCode]);

  // 清空所有代码
  const handleClear = useCallback(() => {
    if (window.confirm('清空后所有代码将丢失，是否继续？')) {
      setCode('');
    }
  }, []);

  // 编辑器内容变化时更新代码
  const handleCodeChange = useCallback((newCode: string) => {
    setCode(newCode);
  }, []);

  return (
    <div className="space-y-4">
      <CppCodeEditor
        code={code}
        starterCode={initialCode}
        onSave={handleSave}
        onReset={handleReset}
        onClear={handleClear}
        onChange={handleCodeChange}
        isSaving={isSaving}
        readOnly={!!submissionId || isSubmitted}
        height="350px"
      />

      {/* 提交成功提示 */}
      {submissionId && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-green-50 rounded-lg border border-green-200 p-4"
        >
          <div className="flex items-center gap-3">
            <CheckCircle className="w-5 h-5 text-green-600" />
            <div>
              <span className="font-bold text-green-700">答案已保存</span>
              <span className="text-sm text-gray-600 ml-2">
                等待教师阅卷
              </span>
            </div>
          </div>
          <p className="text-sm text-gray-500 mt-2">
            您的 C++ 代码已保存，教师将在后台进行人工阅卷或自动判卷。
          </p>
        </motion.div>
      )}

      {isSubmitted && !submissionId && (
        <div className="bg-gray-50 rounded-lg p-4 border border-gray-200 text-center text-gray-500">
          <AlertCircle className="w-8 h-8 mx-auto mb-2 opacity-50" />
          <p>试卷已提交，无法再次编辑代码</p>
        </div>
      )}
    </div>
  );
}
